import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import Recoil from 'recoil';
import React from 'react';
import * as Luxon from 'luxon'
import useRect from 'hooks/common/useRect';
import { Chart, layouts } from 'chart.js';
import { fromFormat } from 'utils/time';

import { Filler } from "chart.js";

Chart.register(Filler);

const columns = Array.from({ length: 25 }).map((_, index) => index.toString().padStart(2, '0') + ':00');
const config = (hash, duration) => ({
  data: {
    labels: columns,
    datasets: [
      {
        type: 'line',
        data: columns.map(_ => 0),
        pointRadius: 0,
        hideInTooltip: true,
      },
      {
        label: T('기저 인슐린'),
        data: hash.base,
        pointRadius: 0,
        stepped: true,
        fill: true,
        borderSkipped: false,
        type: 'line',
        yAxisID: 'y1',
        order: 7,
        backgroundColor: '#ddd9c3',  // 채움 색상
        borderWidth: 1,
        borderColor: '#000000',          // 선 색상
      }, {
        label: T('볼루스 인슐린'),
        barThickness: 12,
        data: hash.bolus,
        borderRadius: [{
          topLeft: 6,
          topRight: 6,
        }],
        borderSkipped: false,
        type: 'bar',
        yAxisID: 'y1',
        order: 6,
        backgroundColor: '#4f81bd',
        borderColor: '#000000',
      }, {
        label: T('섭취칼로리'),
        barThickness: 12,
        data: hash.cal,
        borderRadius: [{
          topLeft: 6,
          topRight: 6,
        }],
        borderSkipped: false,
        yAxisID: 'y2',
        type: 'bar',
        order: 5,
        backgroundColor: '#c3d69b',
        borderColor: '#000000',
      }, {
        label: T('권장 인슐린'),
        pointRadius: 5,
        pointStyle: 'triangle',
        pointRotation: 180,  // 삼각형을 뒤집어 역삼각형으로 만듦
        data: hash.recommend,
        borderRadius: [{
          topLeft: 6,
          topRight: 6,
        }],
        borderSkipped: false,
        type: 'scatter',
        yAxisID: 'y1',
        order: 4,
        backgroundColor: '#ff0000',
        borderColor: '#ff0000',
      }, {
        label: T('자가혈당'),
        pointRadius: 5,
        pointStyle: 'rect',
        data: hash.individual,
        borderRadius: [{
          topLeft: 6,
          topRight: 6,
        }],
        borderSkipped: false,
        type: 'scatter',
        yAxisID: 'y',
        order: 3,
        backgroundColor: '#000000',
        borderColor: '#000000',
      }, {
        label: T('예측혈당'),
        cubicInterpolationMode: 'monotone',
        data: hash.predict,
        pointRadius: 0,
        borderColor: '#ff0000',
        backgroundColor: '#ff0000',
        type: 'line',
        yAxisID: 'y',
        borderWidth: 2,
        order: 1,
      }, {
        label: T('실제혈당'),
        cubicInterpolationMode: 'monotone',
        data: hash.real,
        pointRadius: 0,
        borderColor: '#0000ff',
        backgroundColor: '#0000ff',
        type: 'line',
        yAxisID: 'y',
        borderWidth: 2,
        order: 0,
      }
    ]
  },
  options: {
    devicePixelRatio: 4,
    layout: {
      padding: {
        left: 0,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        border: {
          dash: [4, 4],
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          display: true,
          lineWidth: (context) => {
            switch (columns[context.index]) {
              case 6:
              case 7:
              case 12:
              case 13:
              case 18:
              case 19:
                return 1;
              default: return 0;
            }
          },
        },
        ticks: {
          callback: (value) => {
            if (value % 6 === 0) {
              return value + 'H';
            } else {
              return '';
            }
          },
          color: '#aaaaaa',
          stepSize: 6,
        }
      },
      y: {
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: 1,
          display: true,
        },
        min: 0,
        max: Math.ceil(hash.glocose_max / 50) * 50,
        ticks: {
          color: '#aaaaaa',
          stepSize: 50,
          // display: false,
        },
      },
      y1: {
        type: 'linear',
        max: Math.ceil(hash.insulin_max + 1),
        position: 'right',
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: 0,
          display: true,
        },
        ticks: {
          callback: (value) => {
            return value + 'U';
          },
          color: '#aaaaaa',
          stepSize: 1,
          // display: false,
        },
      },
      y2: {
        max: Math.ceil(hash.cal_max / 100) * 100,
        position: 'right',
        border: {
          display: false  // y2 축의 왼쪽에 보이는 구분선 제거
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: false,
          lineWidth: 0,
          display: true,
        },
        ticks: {
          callback: (value) => {
            return value + 'kcal';
          },
          color: '#aaaaaa',
          stepSize: 100,
          // display: false,
        },
        afterFit: (scale) => {
          scale.width = scale.width + 10;  // 10px 추가
        }
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: tooltipItems => {
            return tooltipItems[0]?.raw?.x;
          },
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y;
            }
            return label;
          },
        },
        filter: function (tooltipItem) {
          // tooltipItem.chart.data.datasets[datasetIndex]를 통해 현재 데이터셋에 접근
          const dataset = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex];
          // 만약 해당 데이터셋에 hideInTooltip이 true라면 tooltip에서 제외(반환값 false)
          return !dataset.hideInTooltip;
        },
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    animation: {
      duration,
      onComplete: (animation) => {
        const chart = animation.chart;
        const ctx = chart.ctx;
        ctx.save();
        // 텍스트 정렬 및 스타일 설정
        ctx.font = '12px bold';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#ff0000';

        // 데이터셋별로 각 데이터 포인트에 텍스트를 그립니다.
        chart.data.datasets.forEach(function (dataset, datasetIndex) {
          if (!dataset.pointRotation) return;
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach(function (element, index) {
            const v = meta._dataset.data[index];
            // element.x와 element.y는 캔버스 상의 해당 데이터 포인트의 좌표입니다.
            let d = 5;
            if (v.y2) {
              ctx.fillText(T('교정 {0}U', v.y2), element.x - 30, element.y - d);
              d += 15;
            }
            if (v.y1) {
              ctx.fillText(T('식사 {0}U', v.y1), element.x - 30, element.y - d);
            }
          });
        });
        ctx.restore();
      }
    }
  },
});

export default function Advice(props) {
  const {
    duration = 1,
    chartData,
    ...rest
  } = props || {};

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();

  const hash = React.useMemo(() => {
    const {
      real_bldsugr_graph = [],
      predict_bldsugr_graph = [],
      self_bldsugr_graph = [],
      recommend_insulin_graph = [],
      bolus_graph = [],
      giju_graph = [],
      intaken_cal_graph = [],
    } = chartData || {};
    const data = {
      base: [],
      recommend: [],
      cal: [],
      bolus: [],
      individual: [],
      predict: [],
      real: [],
      insulin_max: 0,
      glocose_max: 0,
      cal_max: 0,
    };

    real_bldsugr_graph.forEach(({ time: x, bldsugr: y }) => {
      if (data.glocose_max < y) data.glocose_max = y;
      data.real.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y });
    });
    predict_bldsugr_graph.forEach(({ time: x, bldsugr: y }) => {
      if (data.glocose_max < y) data.glocose_max = y;
      data.predict.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y });
    });
    self_bldsugr_graph.forEach(({ time: x, bldsugr: y }) => {
      if (data.glocose_max < y) data.glocose_max = y;
      data.individual.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y });
    });
    recommend_insulin_graph.forEach(({ time: x, meal_insulin_amt: y1, correct_insulin_amt: y2 }) => {
      const y = y1 + y2;
      if (data.insulin_max < y) data.insulin_max = y;
      data.recommend.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y, y1, y2 });
    });
    bolus_graph.forEach(({ time: x, insulin_amt: y }) => {
      if (data.insulin_max < y) data.insulin_max = y;
      data.bolus.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y });
    });
    giju_graph.forEach(({ time: x, insulin_amt: y }, index) => {
      if (index === 12) y = 3;
      if (index === 13) y = 3;
      if (data.insulin_max < y) data.insulin_max = y;
      data.base.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y });
    });
    intaken_cal_graph.forEach(({ time: x, cal_amt: y }) => {
      if (data.cal_max < y) data.cal_max = y;
      data.cal.push({ x: x.replace(/(\d{2})(\d{2})/, '$1:$2'), y });
    });
    return data;
  }, [chartData]);

  React.useEffect(() => {
    const ctx = canvasRef.current.getContext('2d', { willReadFrequently: !duration });
    const backgroundColor = ctx.createLinearGradient(0, 0, 0, 350);
    backgroundColor.addColorStop(0, '#CA99FF');
    backgroundColor.addColorStop(1, '#4CA9FF');
    const settings = config(hash, duration);
    settings.data.datasets[0].backgroundColor = backgroundColor;
    const chart = new Chart(canvasRef.current, settings);
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [duration, hash]);
  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);

  return (
    <div
      {...ex(['Chart', 'AdviceChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}